import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

import { NotificationService } from '@core/services';

import { PersonalTaxFile, StatusProvider } from './models';

export function getImportFailureMessage(statusProvider?: StatusProvider, companyName?: string, statusMessage?: string): string {
    let msg = '';
    switch (statusProvider) {
        case StatusProvider.Silverfin: {
            msg = $localize`:@@declarations.error.importFailed.silverfin:Er ging iets mis in de communicatie met Silverfin bij het importeren voor ${companyName}`;
            break;
        }
        case StatusProvider.Robocorp: {
            msg = $localize`:@@declarations.error.importFailed.robocorp:Er ging iets mis tijdens het verkrijgen van de data van tax-on-web voor ${companyName}`;
            break;
        }
        case StatusProvider.Internal: {
            msg = $localize`:@@declarations.error.importFailed.failed.internal:Er ging iets mis bij het verwerken van de geïmporteerde data voor ${companyName}`;
            break;
        }
        default: {
            msg = $localize`:@@declarations.error.importFailed.named:Het importeren was niet succesvol voor ${companyName}`;
        }
    }
    return statusMessage !== undefined ? `${msg}: ${statusMessage}` : msg;
}

export function getExportFailureMessage(statusProvider?: StatusProvider, companyName?: string, statusMessage?: string): string {
    let msg = '';
    switch (statusProvider) {
        case StatusProvider.Silverfin: {
            msg = $localize`:@@declarations.error.exportFailed.silverfin.named:Er ging iets mis in de communicatie met Silverfin voor het exporteren bij: ${companyName}`;
            break;
        }
        case StatusProvider.Internal: {
            msg = $localize`:@@declarations.error.exportFailed.internal:Er ging iets mis bij het verwerken van de te exoteren data voor ${companyName}`;
            break;
        }
        default: {
            msg = $localize`:@@declarations.error.exportFailed:Het exporteren was niet succesvol voor ${companyName}`;
        }
    }
    return statusMessage !== undefined ? `${msg}: ${statusMessage}` : msg;
}

export function getCalculationFailureMessage(statusProvider?: StatusProvider, companyName?: string, statusMessage?: string): string {
    let msg = '';
    switch (statusProvider) {
        case StatusProvider.Silverfin: {
            msg = $localize`:@@declarations.error.calculationFailed.silverfin:Er ging iets mis in de communicatie met Silverfin bij het berekenen voor ${companyName}`;
            break;
        }
        case StatusProvider.TaxMachine: {
            if (statusMessage !== undefined) {
                const parse = parseTaxMachineMessage(statusMessage);
                statusMessage = `${parse.title} - ${parse.message}`;
            }
            msg = $localize`:@@declarations.error.calculationFailed.taxmachine:Er ging iets mis tijdens het parsen van gegevens of het berekenen voor ${companyName}`;
            break;
        }
        case StatusProvider.Internal: {
            msg = $localize`:@@declarations.error.calculationFailed.failed.internal:Er ging iets mis bij het verwerken van de berekening voor ${companyName}`;
            break;
        }
        default: {
            msg = $localize`:@@declarations.error.calculationFailed.named:De belastingsberekening was niet succesvol voor ${companyName}`;
        }
    }
    return statusMessage !== undefined ? `${msg}: ${statusMessage}` : msg;
}

export function getOptimizationFailureMessage(statusProvider?: StatusProvider, companyName?: string, statusMessage?: string): string {
    let msg = '';
    switch (statusProvider) {
        case StatusProvider.Silverfin: {
            msg = $localize`:@@declarations.error.optimizationFailed.silverfin:Er ging iets mis in de communicatie met Silverfin bij het optimaliseren voor ${companyName}`;
            break;
        }
        case StatusProvider.TaxMachine: {
            if (statusMessage !== undefined) {
                const parse = parseTaxMachineMessage(statusMessage);
                statusMessage = `${parse.title} - ${parse.message}`;
            }
            msg = $localize`:@@declarations.error.optimizationFailed.taxmachine:Er ging iets mis tijdens het parsen van gegevens of het optimaliseren voor ${companyName}`;
            break;
        }
        case StatusProvider.Internal: {
            msg = $localize`:@@declarations.error.optimizationFailed.failed.internal:Er ging iets mis bij het verwerken van de optimalisatie voor ${companyName}`;
            break;
        }
        default: {
            msg = $localize`:@@declarations.error.optimizationFailed.named:De belastingsoptimalisatie was niet succesvol voor ${companyName}`;
        }
    }
    return statusMessage !== undefined ? `${msg}: ${statusMessage}` : msg;
}

export function getParsingFailureMessage(statusProvider?: StatusProvider, companyName?: string, statusMessage?: string): string {
    let msg = '';
    switch (statusProvider) {
        case StatusProvider.Silverfin: {
            msg = $localize`:@@declarations.error.parsingFailed.silverfin:Er ging iets mis bij het versturen van de gegevens naar Silverfin voor ${companyName}`;
            break;
        }
        case StatusProvider.TaxMachine: {
            if (statusMessage !== undefined) {
                const parse = parseTaxMachineMessage(statusMessage);
                statusMessage = `${parse.title} - ${parse.message}`;
            }
            msg = $localize`:@@declarations.error.parsingFailed.taxmachine:Er ging iets mis tijdens het parsen van het bestand voor ${companyName}`;
            break;
        }
        case StatusProvider.Internal: {
            msg = $localize`:@@declarations.error.parsingFailed.failed.internal:Er ging iets mis bij het verwerken het bestand voor ${companyName}`;
            break;
        }
        default: {
            msg = $localize`:@@declarations.error.parsingFailed.named:Importeren uit een bestand was niet succesvol voor ${companyName}`;
        }
    }
    return statusMessage !== undefined ? `${msg}: ${statusMessage}` : msg;
}

export function getBulkExportFailureMessage(statusProvider?: StatusProvider, statusMessage?: string): string {
    let msg = '';
    switch (statusProvider) {
        case StatusProvider.Silverfin: {
            msg = $localize`:@@declarations.error.bulkExportFailed.silverfin:Er ging iets mis in de communicatie met Silverfin tijdens het exporteren in bulk`;
            break;
        }
        case StatusProvider.Internal: {
            msg = $localize`:@@declarations.error.bulkExportFailed.failed.internal:Er ging iets mis bij het verwerken van de bulk export`;
            break;
        }
        default: {
            msg = $localize`:@@declarations.error.bulkExportFailed.named:Exporteren in bulk was niet succesvol`;
        }
    }
    return statusMessage !== undefined ? `${msg}: ${statusMessage}` : msg;
}

export function getLinksSyncFailureMessage(statusProvider?: StatusProvider, statusMessage?: string): string {
    let msg = '';
    switch (statusProvider) {
        case StatusProvider.Silverfin: {
            msg = $localize`:@@declarations.error.linkSyncFailed.silverfin:Er ging iets mis in de communicatie met Silverfin tijdens het syncen van de links`;
            break;
        }
        case StatusProvider.Internal: {
            msg = $localize`:@@declarations.error.linkSyncFailed.failed.internal:Er ging iets mis bij het syncen van de links`;
            break;
        }
        default: {
            msg = $localize`:@@declarations.error.linkSyncFailed.named:Syncen van links was niet succesvol`;
        }
    }
    return statusMessage !== undefined ? `${msg}: ${statusMessage}` : msg;
}

export function getNotificationFailureMessage(
    statusProvider?: StatusProvider,
    companyName?: string,
    statusMessage?: string,
    email?: string,
): string {
    let msg = '';
    switch (statusProvider) {
        case StatusProvider.Silverfin: {
            msg = $localize`:@@declarations.error.sendResultFailed.silverfin:Er ging iets mis in de communicatie met Silverfin bij het verzenden van het resultaat naar ${email} voor ${companyName}`;
            break;
        }
        case StatusProvider.Internal: {
            msg = $localize`:@@declarations.error.sendResultFailed.internal:Er ging iets mis bij het verzenden van het resultaat naar ${email} voor ${companyName}`;
            break;
        }
        default: {
            msg = $localize`:@@declarations.error.sendResultFailed.named:Het verzenden van het resultaat naar ${email} was niet succesvol voor ${companyName}`;
        }
    }
    return statusMessage !== undefined ? `${msg}: ${statusMessage}` : msg;
}

export function getPersonalTaxSyncFailureMessage(statusProvider?: StatusProvider, statusMessage?: string): string {
    let msg = '';
    switch (statusProvider) {
        case StatusProvider.Silverfin: {
            msg = $localize`:@@declarations.error.personalTaxSyncFailed.silverfin:Er ging iets mis in de communicatie met Silverfin bij het synchroniseren`;
            break;
        }
        default: {
            msg = $localize`:@@declarations.error.personalTaxSyncFailed.failed.default:Er ging iets mis bij het synchroniseren van dossiers`;
            break;
        }
    }
    return statusMessage !== undefined ? `${msg}: ${statusMessage}` : msg;
}

export function getPersonalTaxSyncPartialFailureMessage(statusProvider?: StatusProvider, statusMessage?: string): string {
    let msg = '';
    switch (statusProvider) {
        case StatusProvider.Silverfin: {
            msg = $localize`:@@declarations.error.personalTaxSyncPartiallyFailed.silverfin:Er ging iets mis in de communicatie met Silverfin bij het synchroniseren van enkele dossiers`;
            break;
        }
        default: {
            msg = $localize`:@@declarations.error.personalTaxSyncPartiallyFailed.failed.default:Er ging iets mis bij het synchroniseren van enkele dossiers`;
            break;
        }
    }
    return statusMessage !== undefined ? `${msg}: ${statusMessage}` : msg;
}

export function checkSsn(ssn: string) {
    // Ssn with length 12 is pseudo ssn starting with number 2 to check birth > 2000
    // eslint-disable-next-line no-magic-numbers
    return (
        (ssn.length == 11 && 97 - (Number(ssn.slice(0, 9)) % 97) == Number(ssn.slice(9))) ||
        (ssn.length == 12 && 97 - (Number(ssn.slice(0, 10)) % 97) == Number(ssn.slice(10)))
    );
}

export function isValidSsn(ssn?: string) {
    if (ssn === undefined || ssn == '') {
        return true;
    }
    let ssnStripped = ssn.replace(/[^0-9]/g, '');
    return checkSsn(ssnStripped) || checkSsn(`2${ssnStripped}`);
}

export function getSsnWithFullYear(ssn: string) {
    let ssnStripped = ssn.replace(/[^0-9]/g, '');
    let prefix = checkSsn(`2${ssnStripped}`) ? '20' : '19';
    return Number(`${prefix}${ssnStripped}`);
}

export function sortSocialSecurityNumbers(ssn1?: string, ssn2?: string): string[] {
    if ((ssn1 === undefined || ssn1 === '') && (ssn2 === undefined || ssn2 === '')) {
        return [];
    }
    if (ssn1 === undefined || ssn1 === '') {
        return [ssn2 ?? ''];
    }
    if (ssn2 === undefined || ssn2 === '') {
        return [ssn1 ?? ''];
    }
    return getSsnWithFullYear(ssn1) > getSsnWithFullYear(ssn2) ? [ssn2, ssn1] : [ssn1, ssn2];
}

export function filterSocialSecurityNumbers(ssn1?: string, ssn2?: string): string[] {
    return [ssn1?.trim(), ssn2?.trim()].filter((val) => (val ?? '') !== '') as string[];
}

export function socialSecurityNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }
        let value = control.value.replace(/[^0-9]/g, '');
        // eslint-disable-next-line no-magic-numbers
        return checkSsn(`2${value}`) || checkSsn(value) ? null : { invalidSocialSecurityNumber: true };
    };
}

export type TaxMachineParsedError = {
    title: string;
    message: string;
};

export function parseTaxMachineMessage(errorString: string): TaxMachineParsedError {
    // Extract message from errorString as well as preceding error message (title)
    const messageRegex = /\{'message': '(.*)\\n\\nSchema?.*'\}/;
    const titleRegex = /^([\s\S]+?){/;
    const messageMatch = errorString.match(messageRegex);
    const titleMatch = errorString.match(titleRegex);
    const title = titleMatch ? titleMatch[1].trim() : 'Error';
    let message = messageMatch ? messageMatch[1] : errorString;
    message = message?.replace(/\\n/g, '\n').replace(/\\/g, '');
    return { title, message };
}

export function validateAndNotifySsn(file: PersonalTaxFile, localizedAction: string, notificationService: NotificationService) {
    if (
        file.company.socialSecurityNumber === undefined ||
        file.company.socialSecurityNumber === '' ||
        !isValidSsn(file.company.socialSecurityNumber) ||
        (file.socialSecurityNumberOther && !isValidSsn(file.socialSecurityNumberOther))
    ) {
        notificationService.errorMessage(
            $localize`:@@declarations.action.noSSN:Het is niet mogelijk zonder geldig rijksregisternummer te ${localizedAction} voor ${file.company.name}. Gelieve deze eerst in te stellen.`,
        );
        return false;
    }
    return true;
}

export function validateAndNotifySsnInBulk(files: PersonalTaxFile[], localizedAction: string, notificationService: NotificationService) {
    let noSSNFiles = files.filter(
        (f) =>
            f.company.socialSecurityNumber === undefined ||
            f.company.socialSecurityNumber === '' ||
            !isValidSsn(f.company.socialSecurityNumber) ||
            (f.socialSecurityNumberOther && !isValidSsn(f.socialSecurityNumberOther)),
    );
    if (noSSNFiles.length > 0) {
        notificationService.errorMessage(
            $localize`:@@declarations.bulkAction.noSSN:Het is niet mogelijk zonder geldig rijksregisternummer te ${localizedAction} voor ${noSSNFiles
                .map((fileUtil) => fileUtil.company.name)
                .join(', ')}. Gelieve deze eerst in te stellen.`,
        );
        return false;
    }
    return true;
}
